import { Controller } from "@hotwired/stimulus"

// Compte à rebours jusqu'à la fin (ou le début) d'une vente flash. À
// l'échéance, rafraîchit la zone concernée via Turbo (frame si fourni,
// sinon Turbo.visit) plutôt qu'un rechargement complet de la page.
export default class extends Controller {
  static targets = ["value", "hours", "minutes", "seconds", "days", "daysWrap"]
  static values = { endsAt: String, frameId: String }

  disconnect() {
    clearInterval(this.timer)
  }

  // Le sélecteur de taille peut changer la variante affichée, donc la date
  // de fin de la vente flash correspondante (voire la faire disparaître) ;
  // Stimulus rappelle ce hook automatiquement si on réécrit l'attribut.
  endsAtValueChanged() {
    this.start()
  }

  start() {
    clearInterval(this.timer)
    if (!this.endsAtValue) return

    // Échéance DÉJÀ dépassée à l'arrivée : ne jamais rafraîchir ici. La page
    // vient d'être rendue avec cette date — la recharger la rendrait à
    // l'identique tant que le cache de prix n'a pas tourné, et le nouveau
    // compte à rebours redemanderait aussitôt un rafraîchissement : la page
    // se rechargerait en boucle, remontant le défilement à chaque fois. On se
    // contente donc de disparaître, et c'est le rendu serveur suivant qui
    // retirera l'offre.
    if (this.remainingMs() <= 0) {
      this.element.hidden = true
      return
    }

    this.tick()
    this.timer = setInterval(() => this.tick(), 1000)
  }

  remainingMs() {
    return new Date(this.endsAtValue).getTime() - Date.now()
  }

  // Atteindre zéro pendant que la page est ouverte est le cas légitime : on
  // rafraîchit pour que le serveur retire l'offre et rétablisse le prix.
  tick() {
    const remainingMs = this.remainingMs()

    if (remainingMs <= 0) {
      clearInterval(this.timer)
      this.refresh()
      return
    }

    this.renderTarget(remainingMs)
  }

  renderTarget(remainingMs) {
    const totalSeconds = Math.floor(remainingMs / 1000)
    const days = Math.floor(totalSeconds / 86400)
    const hours = Math.floor((totalSeconds % 86400) / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60
    const pad = (n) => String(n).padStart(2, "0")

    // Affichage "segmenté" (jours/heures/minutes/secondes séparés) si les
    // cibles existent, sinon on retombe sur un simple texte "HH:MM:SS".
    if (this.hasHoursTarget && this.hasMinutesTarget && this.hasSecondsTarget) {
      if (this.hasDaysTarget) this.daysTarget.textContent = pad(days)
      if (this.hasDaysWrapTarget) this.daysWrapTarget.hidden = days === 0
      this.hoursTarget.textContent = pad(days > 0 ? hours : hours + days * 24)
      this.minutesTarget.textContent = pad(minutes)
      this.secondsTarget.textContent = pad(seconds)
      return
    }

    const text = `${pad(hours + days * 24)}:${pad(minutes)}:${pad(seconds)}`
    if (this.hasValueTarget) {
      this.valueTarget.textContent = text
    } else {
      this.element.textContent = text
    }
  }

  refresh() {
    if (this.hasFrameIdValue && this.frameIdValue) {
      const frame = document.getElementById(this.frameIdValue)
      if (frame && typeof frame.reload === "function") {
        frame.reload()
        return
      }
    }

    // Rechargement franc plutôt que Turbo.visit : vers la même URL, celui-ci
    // est ignoré — la page restait affichée avec son offre expirée. Le coût
    // est sans importance, l'événement ne se produisant qu'une fois, à la fin
    // de la vente ; et la garde de start() empêche désormais qu'un rendu
    // encore servi depuis le cache ne relance un rechargement en boucle.
    window.location.reload()
  }
}
